import React, { useMemo } from 'react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import ChartWatermarkContainer from 'components/shared/ChartWatermarkContainer';
import D3Plot from 'pages/components/D3Plot';
import { ProfitLossWithDomain } from 'types/riskEngine';
import { useOptionsCalculatorComputed } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';

const Chart = ({ data }: { data: ProfitLossWithDomain }) => {
  const lines = [
    {
      data: data.profitLossData.map((d) => [d.price, d.profit]),
      stroke: '#8888FF',
      fill: 'none',
    },
  ];

  const areas = [
    {
      data: data.profitLossData.map((d) => [d.price, 0, Math.max(d.profit, 0)]),
      fill: '#33FF33',
      opacity: 0.15,
    },
    {
      data: data.profitLossData.map((d) => [d.price, 0, Math.min(d.profit, 0)]),
      fill: '#FF3333',
      opacity: 0.15,
    },
    {
      data: data.profitLossData.map((d) => [
        d.price,
        d.profitBounds[0],
        d.profitBounds[1],
      ]),
      fill: '#3333FF',
      opacity: 0.25,
    },
  ];

  const yValues = data.profitLossData.map((d) => d.profit);

  return (
    <D3Plot
      xDomain={data.domain}
      yDomain={[Math.min(...yValues), Math.max(...yValues)]}
      xAxisLabel="Stock Price ($)"
      yAxisLabel="Profit/Loss ($)"
      lines={lines}
      areas={areas}
      zoomExtent={[0.5, 5]}
      tooltipFormat={(data: number[]) =>
        `<strong>Stock Price:</strong> $${data[0].toFixed(
          2,
        )}<br /><strong>PnL:</strong> $${data[1].toFixed(2)}`
      }
    />
  );
};

const LoadingSkeleton = () => (
  <Box sx={{ width: '100%', height: '300px', p: 2 }}>
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Skeleton variant="text" width="30%" height={30} />
        <Skeleton variant="text" width="20%" height={30} />
      </Box>
      <Box sx={{ display: 'flex', height: '220px' }}>
        <Skeleton
          variant="rectangular"
          width="40px"
          height="100%"
          sx={{ mr: 1 }}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width="100%"
                height="1px"
              />
            ))}
          <Skeleton variant="rectangular" width="100%" height="40px" />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    </Stack>
  </Box>
);

const EmptyState = () => (
  <Box
    sx={{
      width: '100%',
      height: '300px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
    }}
  >
    <Stack spacing={2} alignItems="center">
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1,
        }}
      >
        <TrendingUpIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      </Box>
      <Typography variant="h6" color="text.primary" fontWeight={500}>
        No P&L Data Available
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ maxWidth: 300 }}
      >
        Add options positions to your portfolio to see profit and loss
        calculations across different price scenarios.
      </Typography>
    </Stack>
  </Box>
);

const LoadingOverlay = ({
  isCalculating,
  calculationError,
}: {
  isCalculating: boolean;
  calculationError?: string | null;
}) => {
  const theme = useTheme();
  return (
    <Fade in={isCalculating} timeout={300}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          backdropFilter: 'blur(8px)',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography
          variant="body2"
          sx={{ mt: 2, fontWeight: 500, color: 'primary.main' }}
        >
          Recalculating P&L...
        </Typography>
        {calculationError && (
          <Typography variant="caption" sx={{ mt: 1, color: 'error.main' }}>
            Error: {calculationError}
          </Typography>
        )}
      </Box>
    </Fade>
  );
};

const PnLChart = () => {
  // Only subscribe to the data we actually need
  const { sym, isLoading } = useOptionsCalculatorData();
  const { profitLoss, isCalculating, calculationError, hasValidInputs } =
    useOptionsCalculatorComputed();

  // Memoize the chart content to prevent unnecessary re-renders
  const chartContent = useMemo(() => {
    // Show loading skeleton only for initial data loading (greeks, prices)
    if (isLoading) {
      return <LoadingSkeleton />;
    }

    // Show empty state only when we definitely have no valid inputs and no calculation is running
    if (!hasValidInputs && !isCalculating && !profitLoss) {
      return <EmptyState />;
    }

    // Show chart with overlay if calculating, or just chart if we have data
    return (
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        {profitLoss ? (
          <Chart data={profitLoss} />
        ) : (
          // Show a placeholder while first calculation is running
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">Preparing chart...</Typography>
          </Box>
        )}
        <LoadingOverlay
          isCalculating={isCalculating}
          calculationError={calculationError}
        />
      </Box>
    );
  }, [profitLoss, isLoading, isCalculating, calculationError, hasValidInputs]);

  return (
    <ChartWatermarkContainer size={15} sym={sym}>
      {chartContent}
    </ChartWatermarkContainer>
  );
};

export default PnLChart;
